import { industries } from '@/mocks/homeData';
import { useInView } from '@/hooks/useInView';

function IndustryCard({ industry, delay }: { industry: typeof industries[0]; delay: number }) {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`group p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-300/40 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-500 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-primary-100/80 text-primary-600 mb-5 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
        <i className={`${industry.icon} text-xl md:text-2xl`} />
      </div>
      <h3 className="text-lg md:text-xl font-semibold text-foreground-900 mb-2">
        {industry.name}
      </h3>
      <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
        {industry.description}
      </p>
    </div>
  );
}

export default function IndustriesSection() {
  const [titleRef, titleInView] = useInView<HTMLDivElement>();

  return (
    <section id="industries" className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 bg-background-100">
      {/* Header */}
      <div
        ref={titleRef}
        className={`text-center max-w-2xl mx-auto mb-10 md:mb-14 transition-all duration-700 ${
          titleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-secondary-100 rounded-full mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-secondary-500" />
          <span className="text-xs font-medium text-secondary-700 uppercase tracking-wider">Industries</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-3">
          Built for high-trust industries
        </h2>
        <p className="text-base md:text-lg text-foreground-600">
          Ardeun biometric solutions are deployed across sectors where identity certainty is non-negotiable.
        </p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {industries.map((industry, i) => (
          <IndustryCard key={industry.id} industry={industry} delay={i * 100} />
        ))}
      </div>
    </section>
  );
}